import React, { useState, useEffect, useRef, useMemo } from 'react';
import './Activities.css';
import api, { API_BASE_URL } from '../api';
import {
  enrichActivities,
  formatActivityTimeRange,
  getActivitySortTime,
} from '../utils/activityUtils';

function Activities({ mouPrefix, mou, userEmail, canManage }) {
  const data = mou?.json || mou?.data || {};
  const successMetricsText = data.success_metrics || '';
  let kpiNames = [];
  if (successMetricsText) {
    kpiNames = successMetricsText
      .split(/[,;]|\sand\s/i)
      .map(item => item.trim())
      .filter(item => item.length > 0);
  }
  if (kpiNames.length === 0) {
    kpiNames = data.kpis || data.key_highlights || [];
  }
  const [activities, setActivities] = useState([]);
  const [kpis, setKpis] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [now, setNow] = useState(() => new Date());
  const fileInputsRef = useRef({});
  const [newActivity, setNewActivity] = useState({
    title: '',
    description: '',
    date: '',
    startTime: '10:00',
    endTime: '16:00',
  });

  useEffect(() => {
    fetchActivities();
    fetchKpis();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mouPrefix]);

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(timer);
  }, []);

  const fetchActivities = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/api/mous/${mouPrefix}/activities`);
      setActivities(response.data);
    } catch (err) {
      console.error('Error fetching activities:', err);
      setActivities([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchKpis = async () => {
    try {
      const response = await api.get(`/api/mous/${mouPrefix}/kpis`);
      setKpis(response.data || []);
    } catch (err) {
      console.error('Error fetching KPIs:', err);
    }
  };

  const enrichedActivities = useMemo(
    () =>
      [...enrichActivities(activities, now)].sort(
        (a, b) => getActivitySortTime(b) - getActivitySortTime(a)
      ),
    [activities, now]
  );

  const handleAddActivity = async () => {
    if (!newActivity.title || !newActivity.date || !newActivity.startTime || !newActivity.endTime) {
      alert('Title, date, start time, and end time are required');
      return;
    }

    if (newActivity.endTime <= newActivity.startTime) {
      alert('End time must be after start time');
      return;
    }

    try {
      await api.post(`/api/mous/${mouPrefix}/activities`, {
        title: newActivity.title,
        description: newActivity.description,
        date: newActivity.date,
        startTime: newActivity.startTime,
        endTime: newActivity.endTime,
        status: 'not-started',
        createdBy: userEmail,
        createdAt: new Date().toISOString(),
        kpiIndex: newActivity.kpiIndex !== undefined && newActivity.kpiIndex !== '' ? Number(newActivity.kpiIndex) : null,
      });

      setNewActivity({
        title: '',
        description: '',
        date: '',
        startTime: '10:00',
        endTime: '16:00',
      });
      setIsAdding(false);
      fetchActivities();
    } catch (err) {
      console.error('Error adding activity:', err);
      alert(err.response?.data?.error || 'Failed to add activity');
    }
  };

  const handleCancelActivity = async (activityId) => {
    if (!window.confirm('Cancel this activity? It cannot be restarted once cancelled.')) {
      return;
    }

    try {
      await api.patch(`/api/mous/${mouPrefix}/activities/${activityId}`, {
        status: 'cancelled',
      });
      fetchActivities();
    } catch (err) {
      console.error('Error cancelling activity:', err);
      alert(err.response?.data?.error || 'Failed to cancel activity');
    }
  };

  const handleDeleteActivity = async (activityId) => {
    if (!window.confirm('Are you sure you want to delete this activity?')) {
      return;
    }

    try {
      await api.delete(`/api/mous/${mouPrefix}/activities/${activityId}`);
      fetchActivities();
    } catch (err) {
      console.error('Error deleting activity:', err);
      alert('Failed to delete activity');
    }
  };

  const handleUploadPhoto = async (activityId, file) => {
    if (!file) return;

    const formData = new FormData();
    formData.append('photo', file);
    formData.append('uploadedBy', userEmail || 'unknown');

    try {
      await api.post(`/api/mous/${mouPrefix}/activities/${activityId}/attachments`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      fetchActivities();
    } catch (err) {
      console.error('Error uploading photo:', err);
      alert(err.response?.data?.error || 'Failed to upload photo');
    } finally {
      if (fileInputsRef.current[activityId]) {
        fileInputsRef.current[activityId].value = '';
      }
    }
  };

  const handleDeletePhoto = async (activityId, attachmentId) => {
    if (!window.confirm('Are you sure you want to delete this photo?')) {
      return;
    }

    try {
      await api.delete(`/api/mous/${mouPrefix}/activities/${activityId}/attachments/${attachmentId}`);
      fetchActivities();
    } catch (err) {
      console.error('Error deleting photo:', err);
      alert('Failed to delete photo');
    }
  };

  const isLikedByUser = (activity) => {
    if (!userEmail) return false;
    return Array.isArray(activity.likes) && activity.likes.includes(userEmail);
  };

  const toggleLike = async (activity) => {
    try {
      if (isLikedByUser(activity)) {
        await api.delete(`/api/mous/${mouPrefix}/activities/${activity.id}/like`, {
          params: { email: userEmail },
        });
      } else {
        await api.post(`/api/mous/${mouPrefix}/activities/${activity.id}/like`, {
          email: userEmail,
        });
      }
      fetchActivities();
    } catch (err) {
      console.error('Error toggling like:', err);
    }
  };

  const handleRateActivity = async (activity, rating) => {
    if (!userEmail) {
      alert('You must be logged in to rate an activity.');
      return;
    }
    try {
      await api.post(`/api/mous/${mouPrefix}/activities/${activity.id}/rate`, {
        email: userEmail,
        rating,
      });
      fetchActivities();
    } catch (err) {
      console.error('Error rating activity:', err);
      alert('Failed to rate activity');
    }
  };

  const calculateAverageRating = (ratings = {}) => {
    const values = Object.values(ratings);
    if (values.length === 0) return 0;
    return (values.reduce((a, b) => a + b, 0) / values.length).toFixed(1);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'green';
      case 'in-progress':
        return 'blue';
      case 'not-started':
        return 'orange';
      case 'cancelled':
        return 'red';
      default:
        return 'gray';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'not-started':
        return 'Not started';
      case 'in-progress':
        return 'In progress';
      case 'completed':
        return 'Completed';
      case 'cancelled':
        return 'Cancelled';
      default:
        return status;
    }
  };

  if (loading) {
    return <div className="activities-loading">Loading activities...</div>;
  }

  return (
    <div className="activities-container">
      <div className="activities-header">
        <h3>📅 Activities & Events</h3>
        {canManage && (
          <button
            className="btn-add-activity"
            onClick={() => setIsAdding(!isAdding)}
          >
            {isAdding ? '✕ Cancel' : '➕ Add Activity'}
          </button>
        )}
      </div>

      {isAdding && (
        <div className="activity-form">
          <input
            type="text"
            placeholder="Activity Title *"
            value={newActivity.title}
            onChange={(e) => setNewActivity({ ...newActivity, title: e.target.value })}
            className="form-input"
          />
          <textarea
            placeholder="Description"
            value={newActivity.description}
            onChange={(e) => setNewActivity({ ...newActivity, description: e.target.value })}
            className="form-textarea"
            rows="3"
          />
          <div className="form-row form-row-three">
            <input
              type="date"
              value={newActivity.date}
              onChange={(e) => setNewActivity({ ...newActivity, date: e.target.value })}
              className="form-input"
            />
            <input
              type="time"
              value={newActivity.startTime}
              onChange={(e) => setNewActivity({ ...newActivity, startTime: e.target.value })}
              className="form-input"
              title="Start time"
            />
            <input
              type="time"
              value={newActivity.endTime}
              onChange={(e) => setNewActivity({ ...newActivity, endTime: e.target.value })}
              className="form-input"
              title="End time"
            />
          </div>
          {kpis.length > 0 && (
            <select
              className="form-input"
              value={newActivity.kpiIndex !== undefined ? newActivity.kpiIndex : ''}
              onChange={(e) => setNewActivity({ ...newActivity, kpiIndex: e.target.value })}
            >
              <option value="">-- Associate with a KPI (Optional) --</option>
              {kpis.map((kpi, index) => {
                const name = kpiNames[index] || `KPI ${index + 1}`;
                return (
                  <option key={index} value={index}>
                    {name} (Current: {kpi.completed || 0}/{kpi.planned || 0})
                  </option>
                );
              })}
            </select>
          )}
          <p className="form-hint">
            Progress is tracked automatically between the start and end time.
          </p>
          <button className="btn-save-activity" onClick={handleAddActivity}>
            Save Activity
          </button>
        </div>
      )}

      <div className="activities-list timeline">
        {enrichedActivities.length === 0 ? (
          <div className="no-activities">
            📭 No activities yet. {canManage && 'Click "Add Activity" to create one.'}
          </div>
        ) : (
          enrichedActivities.map((activity) => (
            <div
              key={activity.id}
              className={`activity-card ${activity.isLocked ? 'activity-locked' : ''}`}
            >
              <div className="activity-header">
                <h4>{activity.title}</h4>
                <span className={`status-badge ${getStatusColor(activity.effectiveStatus)}`}>
                  {getStatusLabel(activity.effectiveStatus)}
                </span>
              </div>

              {activity.description && (
                <p className="activity-description">{activity.description}</p>
              )}

              <div className="activity-meta">
                <span>🕒 {formatActivityTimeRange(activity)}</span>
                <span>👤 {activity.createdBy}</span>
              </div>

              {activity.effectiveStatus !== 'cancelled' && (
                <div className="activity-progress">
                  <div className="progress-labels">
                    <span>Progress</span>
                    <span>{activity.progress}%</span>
                  </div>
                  <div className="progress-track">
                    <div
                      className={`progress-fill ${activity.effectiveStatus}`}
                      style={{ width: `${activity.progress}%` }}
                    />
                  </div>
                </div>
              )}

              {activity.attachments && activity.attachments.length > 0 && (
                <div className="activity-attachments">
                  {activity.attachments.map((att) => (
                    <div key={att.id} className="attachment-wrapper">
                      <a
                        href={`${att.url.startsWith('http') ? att.url : API_BASE_URL + att.url}`}
                        target="_blank"
                        rel="noreferrer"
                        className="attachment-thumb"
                      >
                        <img
                          src={`${att.url.startsWith('http') ? att.url : API_BASE_URL + att.url}`}
                          alt={att.originalName || 'attachment'}
                        />
                      </a>
                      {canManage && (
                        <button
                          className="btn-delete-photo"
                          onClick={() => handleDeletePhoto(activity.id, att.id)}
                          title="Remove Photo"
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}

              <div className="activity-actions">
                <button
                  className={`btn-like ${isLikedByUser(activity) ? 'liked' : ''}`}
                  onClick={() => toggleLike(activity)}
                >
                  {isLikedByUser(activity) ? '❤️' : '🤍'}{' '}
                  {Array.isArray(activity.likes) ? activity.likes.length : 0}
                </button>

                {canManage && !activity.isLocked && activity.effectiveStatus !== 'cancelled' && (
                  <button
                    className="btn-cancel-activity"
                    onClick={() => handleCancelActivity(activity.id)}
                  >
                    ✕ Cancel Event
                  </button>
                )}

                {canManage && activity.canUploadPhotos && (
                  <label className="btn-upload">
                    📷 Add Photo
                    <input
                      type="file"
                      accept="image/*"
                      hidden
                      ref={(el) => {
                        fileInputsRef.current[activity.id] = el;
                      }}
                      onChange={(e) => handleUploadPhoto(activity.id, e.target.files?.[0])}
                    />
                  </label>
                )}

                {canManage && activity.effectiveStatus === 'completed' && !activity.canUploadPhotos && (
                  <span className="photo-window-closed">Photo upload closed</span>
                )}

                {canManage && (
                  <button
                    className="btn-delete"
                    onClick={() => handleDeleteActivity(activity.id)}
                  >
                    🗑️ Delete
                  </button>
                )}
              </div>
              
              {activity.effectiveStatus === 'completed' && (
                <div className="activity-rating">
                  <div className="rating-info">
                    <span className="avg-rating">
                      {calculateAverageRating(activity.ratings)} ⭐
                    </span>
                    <span className="rating-count">
                      ({Object.keys(activity.ratings || {}).length} ratings)
                    </span>
                  </div>
                  <div className="star-rating">
                    {[1, 2, 3, 4, 5].map((star) => {
                      const userRating = (activity.ratings || {})[userEmail];
                      return (
                        <button
                          key={star}
                          className={`btn-star ${userRating >= star ? 'filled' : ''}`}
                          onClick={() => handleRateActivity(activity, star)}
                        >
                          ★
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default Activities;
